/**
 * BCW Cart Drawer — portable web component
 * Replaces Dawn's cart-drawer.js with identical API
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });
    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.bcw-drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  renderContents(parsedState) {
    if (typeof parsedState.item_count === 'number') {
      this.classList.toggle('is-empty', parsedState.item_count === 0);
    }
    this.productId = parsedState.id;

    // Preserve state across section re-render (prevents flashing to stale Liquid values)
    const oldStatusText = (this.querySelector('.bcw-drawer__progress-status') || {}).textContent || '';
    const oldFills = Array.from(this.querySelectorAll('.bcw-drawer__progress-fill')).map(
      (f) => f.style.width
    );
    const oldMilestoneReached = Array.from(this.querySelectorAll('[data-milestone]')).map((s) =>
      s.querySelector('.bcw-drawer__milestone')?.classList.contains('bcw-drawer__milestone--reached')
    );

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    // Re-apply preserved state so the UI doesn't flicker before updateProgressBar runs
    const statusEl = this.querySelector('.bcw-drawer__progress-status');
    if (statusEl && oldStatusText) statusEl.textContent = oldStatusText;

    const newFills = this.querySelectorAll('.bcw-drawer__progress-fill');
    newFills.forEach((fill, i) => {
      const oldWidth = oldFills[i];
      if (oldWidth != null) {
        fill.style.transition = 'none';
        fill.style.width = oldWidth;
        requestAnimationFrame(() => {
          fill.style.transition = '';
        });
      }
    });

    const newSegments = this.querySelectorAll('[data-milestone]');
    newSegments.forEach((seg, i) => {
      const m = seg.querySelector('.bcw-drawer__milestone');
      if (m && typeof oldMilestoneReached[i] === 'boolean') {
        m.classList.toggle('bcw-drawer__milestone--reached', oldMilestoneReached[i]);
      }
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();

      // Re-init upsells after section re-render
      const upsells = this.querySelector('bcw-cart-upsells');
      if (upsells) upsells.refresh();

      this.syncFreeGift();
      this.updateProgressBar();
    });

    const expectedCount = parsedState.item_count;
    if (typeof expectedCount === 'number') {
      setTimeout(() => this._refreshIfCartDiverged(expectedCount), 1500);
    }
  }

  async _refreshIfCartDiverged(expectedCount) {
    try {
      const cart = await fetch(`${window.routes.cart_url}.js`).then((r) => r.json());
      if (cart.item_count === expectedCount) return;
      const resp = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
      const html = await resp.text();
      const dom = new DOMParser().parseFromString(html, 'text/html');
      const newInner = dom.querySelector('#CartDrawer');
      const currentInner = document.querySelector('#CartDrawer');
      if (newInner && currentInner) currentInner.innerHTML = newInner.innerHTML;
      this.classList.toggle('is-empty', cart.item_count === 0);
      this.syncFreeGift();
      this.updateProgressBar();
    } catch (e) {
      console.error('BCW drawer refresh:', e);
    }
  }

  connectedCallback() {
    this.syncFreeGift();
    this.updateProgressBar();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.syncFreeGift();
        this.updateProgressBar();
      });
    }
  }

  async updateProgressBar() {
    const shippingThreshold = parseInt(this.dataset.shippingThreshold || '0', 10);
    const giftThreshold = parseInt(this.dataset.giftThreshold || '0', 10);
    if (!shippingThreshold && !giftThreshold) return;
    try {
      const cart = await fetch(`${window.routes.cart_url}.js`).then((r) => r.json());
      const rate = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '1');
      const effectiveShipping = shippingThreshold * rate;
      const effectiveGift = giftThreshold * rate;
      this._updateSegment('shipping', effectiveShipping, cart.total_price);
      this._updateSegment('gift', effectiveGift, cart.total_price);
      this._updateStatus(cart.total_price, effectiveShipping, effectiveGift);
    } catch (e) {
      console.error('BCW progress bar:', e);
    }
  }

  _updateStatus(total, effectiveShipping, effectiveGift) {
    const el = this.querySelector('.bcw-drawer__progress-status');
    if (!el) return;
    const template = this.dataset.statusTemplate || 'Spend %amount% more for %reward%';
    const allUnlocked = this.dataset.statusAllUnlocked || "You've unlocked all rewards!";
    const shippingLabel = this.dataset.shippingLabel || '';
    const giftLabel = this.dataset.giftLabel || '';
    let remaining = 0;
    let reward = '';
    if (effectiveShipping > 0 && total < effectiveShipping) {
      remaining = effectiveShipping - total;
      reward = shippingLabel;
    } else if (effectiveGift > 0 && total < effectiveGift) {
      remaining = effectiveGift - total;
      reward = giftLabel;
    } else {
      el.textContent = allUnlocked;
      return;
    }
    el.textContent = template.replace('%amount%', this._formatMoney(remaining)).replace('%reward%', reward);
  }

  _formatMoney(cents) {
    const active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (active) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: active,
        }).format(cents / 100);
      } catch (e) {}
    }
    return (cents / 100).toFixed(2);
  }

  _updateSegment(milestone, effectiveThreshold, total) {
    const segment = this.querySelector(`[data-milestone="${milestone}"]`);
    if (!segment) return;
    const fill = segment.querySelector('.bcw-drawer__progress-fill');
    const milestoneEl = segment.querySelector('.bcw-drawer__milestone');
    let pct;
    let reached;
    if (effectiveThreshold > 0) {
      reached = total >= effectiveThreshold;
      pct = reached ? 100 : (total * 100) / effectiveThreshold;
    } else {
      reached = true;
      pct = 100;
    }
    if (fill) fill.style.width = `${pct}%`;
    if (milestoneEl) milestoneEl.classList.toggle('bcw-drawer__milestone--reached', reached);
  }

  disconnectedCallback() {
    if (this._cartUpdateUnsub) this._cartUpdateUnsub();
  }

  async syncFreeGift() {
    if (this._giftSyncing) return;
    const giftVariantId = parseInt(this.dataset.giftVariantId, 10);
    const threshold = parseInt(this.dataset.giftThreshold, 10);
    if (!giftVariantId) return;

    this._giftSyncing = true;
    try {
      const cart = await fetch(`${window.routes.cart_url}.js`).then((r) => r.json());

      if (cart.items.length > 0 && cart.total_price === 0) {
        await this._clearCart();
        return;
      }

      const giftItem = cart.items.find((i) => i.variant_id === giftVariantId);
      const rate = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '1');
      const effectiveThreshold = threshold * rate;
      const reached = threshold > 0 ? cart.total_price >= effectiveThreshold : true;

      if (reached && !giftItem && cart.item_count > 0) {
        await this._addGift(giftVariantId);
      } else if (!reached && giftItem) {
        await this._removeGift(giftItem.key);
      }
    } catch (e) {
      console.error('BCW gift sync:', e);
    } finally {
      this._giftSyncing = false;
    }
  }

  async _addGift(variantId) {
    const response = await fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: { _bcw_gift: 'milestone_2' },
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    if (data.status === 422) return;
    this.renderContents(data);
  }

  async _removeGift(lineKey) {
    const response = await fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    this.renderContents(data);
  }

  async _clearCart() {
    const response = await fetch(`${window.routes.cart_url}/clear.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    this.renderContents(data);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  onChange(event) {
    if (event.target.closest('bcw-cart-upsells')) return;
    super.onChange(event);
  }

  enableLoading(line) {
    super.enableLoading(line);
    const item = this.querySelector(`#CartDrawer-Item-${line}`);
    if (item) item.classList.add('is-loading');
  }

  disableLoading(line) {
    super.disableLoading(line);
    const item = this.querySelector(`#CartDrawer-Item-${line}`);
    if (item) item.classList.remove('is-loading');
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
